import { hashKey, useQuery } from '@tanstack/react-query'

import { type ShipmentOrdersRequestQuery } from '@/libs/api/types'

import { fetchShipmentOrdersSummary } from '../api'
import { shipmentOrdersQueryKeys } from '../query-keys'

type UseShipmentOrdersSummaryQueryParams = {
  tenantKey: string
  /** 一覧に渡しているのと同じクエリ（queryFrom の結果） */
  query: ShipmentOrdersRequestQuery
}

/**
 * サマリーを「新しい」とみなす時間。
 *
 * 一覧ページは検索条件（page / take / sort / order を含む）が変わるたびに
 * ルートの match が作り直され、ページのコンポーネントが再マウントされる
 * （match の id に loaderDeps のハッシュが入り、それが React の key になる）。
 * 既定の staleTime 0 だと再マウントのたびに集計 API（最も重いクエリ）が走る。
 * 実測ではページ送り 4 回で集計 API が 1 回 → 5 回に増えたので、
 * ページ移動や並び替えでは取り直さないよう、しばらくは新しいとみなす。
 * 鮮度は次の 2 つで担保する。
 * - 検索の submit（route.tsx が同じ条件のときに invalidate する）
 * - 依頼の件数や内容を変える操作（useInvalidateShipmentOrdersSummary が invalidate する）
 */
export const SHIPMENT_ORDERS_SUMMARY_STALE_TIME_MS = 5 * 60 * 1000

/**
 * 一覧のクエリからサマリー用のクエリを作る。
 * page / take / sort / order は集計に関係ないので除く。queryKey も API の引数もこの値を使い、
 * ページ移動・表示件数・並び替えではキーが変わらない
 */
export const toSummaryQuery = (
  query: ShipmentOrdersRequestQuery
): ShipmentOrdersRequestQuery => {
  const {
    page: _page,
    take: _take,
    sort: _sort,
    order: _order,
    ...rest
  } = query ?? {}
  void _page
  void _take
  void _sort
  void _order
  return rest
}

/**
 * 2 つの一覧クエリが同じサマリーを指すか。
 *
 * 判定したいのは「サマリーの queryKey が同じか」なので、react-query が
 * キャッシュのキーを作るのと同じ規則（hashKey）で比べる。hashKey は
 * オブジェクトのプロパティ名を並べ替えてから文字列化するため、
 * 検索スキーマとフォームの初期値でプロパティの順が違っても同じ結果になる。
 * JSON.stringify で比べると順の違いで別物と判定し、invalidate を取りこぼす
 */
export const isSameSummaryQuery = (
  tenantKey: string,
  a: ShipmentOrdersRequestQuery,
  b: ShipmentOrdersRequestQuery
): boolean =>
  hashKey(shipmentOrdersQueryKeys.summary(tenantKey, toSummaryQuery(a))) ===
  hashKey(shipmentOrdersQueryKeys.summary(tenantKey, toSummaryQuery(b)))

/**
 * 検索結果サマリー（荷姿数・数量・重量）を取る（#5285 2.1）
 */
export const useShipmentOrdersSummaryQuery = ({
  tenantKey,
  query
}: UseShipmentOrdersSummaryQueryParams) => {
  const summaryQuery = toSummaryQuery(query)
  return useQuery({
    queryKey: shipmentOrdersQueryKeys.summary(tenantKey, summaryQuery),
    queryFn: ({ signal }) =>
      fetchShipmentOrdersSummary({ tenantKey, query: summaryQuery }, signal),
    staleTime: SHIPMENT_ORDERS_SUMMARY_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false
  })
}
